import React, { Component } from 'react'
import { Formik, Form, Field } from 'formik'
import { inject } from 'mobx-react'
import yup from 'yup'
import get from 'lodash/get'
import find from 'lodash/find'
import mapValues from 'lodash/mapValues'
import mapKeys from 'lodash/mapKeys'
import isObject from 'lodash/isObject'
import PropTypes from 'prop-types'
import moment from 'moment'

import { createGeneralAdjustment, configurationApi, contractApi, accountApi, parentAccountApi, projectApi, invoicingEntityApi } from '@atheneum/api'
import WarningIcon from '@components/Icon/Warning'
import Button from '@components/Button'
import InfoBox from '@components/InfoBox'
import Input from '@components/Form/Input'
import TextArea from '@components/Form/TextArea'
import DatePicker from '@components/Form/DatePicker'
import Spinner from '@components/spinner'
import { Autocomplete, Dropdown } from '@components/Form/Autocomplete'
import { convertStaticDataToOptions, precision } from '@atheneum/api/dist/lib'

class GeneralAdjustmentForm extends Component {
  static propTypes = {
    financialRevenueStore: PropTypes.object,
    modalStore: PropTypes.object,
    growlStore: PropTypes.object,
    staticData: PropTypes.object,
    atheneumOfficeStore: PropTypes.object
  }

  constructor (props) {
    super(props)

    this.state = {
      error: null,
      loading: true,
      bookingStopDate: null
    }

    this.getBookingStopDate()
  }

  getBookingStopDate = async () => {
    try {
      const bookingStopDate = await configurationApi.getByKey('BOOKING_STOP_DATE')
      this.setState({
        bookingStopDate: bookingStopDate.value,
        error: null,
        loading: false
      })
    } catch (err) {
      this.setState({
        error: err,
        loading: false
      })
    }
  }

  handleParentAccountAutocomplete = input => {
    return parentAccountApi.typeaheadSearch(input).then(results =>
      results.map(({ id, parentAccountName }) => ({
        value: id,
        label: parentAccountName
      }))
    )
  }

  handleAccountAutocomplete = form => {
    const parentAccountId = get(form.values, 'parentAccount.value')
    return accountApi.getAccountsByParentAccount(parentAccountId)
      .then(results =>
        results.map(({ id, companyName }) => ({
          value: id,
          label: companyName
        }))
      )
  }

  handleProjectAutocomplete = (input, form) => {
    return projectApi
      .typeaheadSearch(input, {
        accountId: get(form.values, 'account.value')
      })
      .then(results => {
        return results.map(({ projectName, id, projectTypeId }) => ({
          value: id,
          label: projectName,
          projectTypeId
        }))
      })
  }

  handleContractAutocomplete = (input, form) => {
    const parentAccountId = get(form.values, 'parentAccount.value')

    return contractApi.typeahead(input, parentAccountId).then(results =>
      results.map(({ id, contractReference, contractTypeId }) => ({
        value: id,
        label: contractReference,
        contractTypeId
      }))
    )
  }

  handleInvoicinigEntityAutocomplete = (input, form) => {
    const parentAccountId = get(form.values, 'parentAccount.value')

    return invoicingEntityApi.typeahead(input, parentAccountId).then(results =>
      results.map(invoicingEnity => ({
        value: invoicingEnity.id,
        label: invoicingEnity.legalName,
        ...invoicingEnity
      }))
    )
  }

  handleParentAccountChange = (parentAccount, form) => {
    form.setFieldValue('parentAccount', parentAccount, false)
    form.setFieldTouched('parentAccount', true, true)
    form.setFieldValue('account', null)
    form.setFieldValue('contract', null)
    form.setFieldValue('invoicingEntity', null)
  }

  handleAccountChange = (account, form) => {
    form.setFieldValue('account', account, false)
    form.setFieldTouched('account', true, true)
    form.setFieldValue('project', null)
  }

  handleProjectChange = (project, form) => {
    form.setFieldValue('project', project, false)
    form.setFieldTouched('project', true, true)
    form.setFieldValue('projectTypeId', get(project, 'projectTypeId'))
  }

  handleContractChange = (contract, form) => {
    const contractTypeId = get(contract, 'contractTypeId')
    form.setFieldValue('contract', contract, false)
    form.setFieldTouched('contract', true, true)

    if (contractTypeId) {
      form.setFieldValue('contractTypeId', get(contract, 'contractTypeId'))
    }
  }

  handleInvoicingEntityChange = (invoicingEntity, form) => {
    const { countries } = this.props.staticData
    const countryId = get(invoicingEntity, 'countryId')

    form.setFieldValue('invoicingEntity', invoicingEntity, false)
    form.setFieldTouched('invoicingEntity', true, true)

    if (countryId) {
      const { id, name } = find(countries, { id: countryId })
      form.setFieldValue('invoicingEntityCountry', { value: id, label: name })
    }
  }

  renderInvoicingEntity = invoicingEntity => {
    // Show nothing when no inv. entity selected
    if (!invoicingEntity || !invoicingEntity.id) return ''

    const { city, countryId } = invoicingEntity.address
    const countryNames = this.props.staticData.names.countries
    const country = countryNames[countryId]

    const name = [invoicingEntity.legalName, city, country]
      .filter(namePart => namePart != null)
      .join(', ')

    return name
  }

  handleFormSubmission = (values, actions) => {
    this.setState({ error: null })

    const preparedValues = mapValues(values, item =>
      isObject(item) ? item.value : item
    )

    const renamedKeys = mapKeys(preparedValues, (v, k) => {
      if (
        [
          'parentAccount',
          'account',
          'project',
          'invoicingEntityCountry',
          'invoicingEntity',
          'contract'
        ].includes(k)
      ) {
        return `${k}Id`
      }

      return k
    })

    return createGeneralAdjustment(renamedKeys)
      .then(createdAdjustment => {
        this.props.modalStore.close()
        this.props.growlStore.addNotification({
          message: `General adjustment #${
            createdAdjustment.serialFormatted
          } created successfuly.`,
          autoClose: true,
          title: 'Saved',
          type: 'success'
        })

        this.props.financialRevenueStore.filter()
      })
      .catch(err => {
        this.props.growlStore.showError(err)

        this.setState({ error: err.message })
        actions.setSubmitting(false)
      })
  }

  initialValues = {
    documentDate: null,
    amount: null,
    currencyId: null,
    description: null,
    parentAccount: null,
    account: null,
    contract: null,
    contractTypeId: null,
    projectTypeId: null,
    invoicingEntityCountry: null
  }

  getValidationSchema () {
    const optionSchema = fieldName => {
      return yup
        .object()
        .typeError(
          `${fieldName} is required, select one of the suggested items`
        )
        .shape({
          value: yup
            .number()
            .typeError(
              `${fieldName} is required, select one of the suggested items`
            )
        })
    }

    return yup.object().shape({
      documentDate: yup
        .date()
        .typeError('Document date is required')
        .required('Document date is required'),
      amount: yup
        .number()
        .test(
          'two-decimal-places',
          'Number can contain only 2 decimal places. (e.g. 33.20)',
          precision(2)
        )
        .required('Amount is required')
        .typeError('Amount must be a number'),
      currencyId: yup
        .number()
        .typeError('Currency is required')
        .required('Currency is required'),
      description: yup.string().nullable(),
      parentAccount: optionSchema('Parent account').nullable(),
      account: optionSchema('Account').nullable(),
      project: optionSchema('Project').nullable(),
      contract: optionSchema('Contract').nullable(),
      atheneumOfficeId: yup.number().nullable(),
      contractTypeId: yup.number().nullable(),
      projectTypeId: yup.number().nullable(),
      invoicingEntity: optionSchema('Invoicing entity').nullable()
    })
  }

  render () {
    const { modalStore, staticData, atheneumOfficeStore } = this.props
    const { atheneumOffices } = atheneumOfficeStore
    const formError = this.state.error

    const maxDocumentDate = moment()
      .endOf('month')
      .toDate()

    if (this.state.loading) return <Spinner />

    return (
      <Formik
        initialValues={this.initialValues}
        validationSchema={this.getValidationSchema()}
        onSubmit={this.handleFormSubmission}
        render={form => (
          <div className='expert-form'>
            <h1 className='expert-form__title'>Create general adjustment</h1>
            {formError ? (
              <InfoBox theme='red' icon={<WarningIcon />}>
                {formError}
              </InfoBox>
            ) : null}
            <Form onSubmit={form.handleSubmit}>
              <div className='expert-form__section'>
                <Field
                  short
                  name='documentDate'
                  label={required('Document date')}
                  dateOnly
                  minDate={this.state.bookingStopDate}
                  maxDate={maxDocumentDate}
                  component={DatePicker}
                />
                <div className='expert-form__input-group'>
                  <label className='expert-form__label'>
                    {required('Amount')}
                  </label>
                  <div className='expert-form__input-wrapper expert-form__input-wrapper--two_one_children'>
                    <Field
                      name='amount'
                      placeholder='Amount'
                      component={Input}
                    />
                    <Field
                      noLabel
                      name='currencyId'
                      placeholder='Select currency'
                      itemToString={item => item && item.name}
                      options={staticData.currencies}
                      component={Dropdown}
                    />
                    <div />
                  </div>
                </div>
                <Field
                  name='description'
                  label='Description'
                  component={TextArea}
                />
                <Field
                  name='atheneumOfficeId'
                  label='Atheneum office'
                  itemToString={item => item && item.name}
                  options={atheneumOffices}
                  component={Dropdown}
                />
                <Field
                  name='projectTypeId'
                  label='Project type'
                  itemToString={item => item && item.name}
                  options={staticData.projectTypes}
                  disabled={get(form.values, 'project.value')}
                  component={Dropdown}
                />
                <Field
                  name='contractTypeId'
                  label='Contract type'
                  itemToString={item => item && item.name}
                  options={staticData.contractTypes}
                  disabled={get(form.values, 'contract.value')}
                  component={Dropdown}
                />
                <Field
                  name='invoicingEntityCountry'
                  label='Invoicing entity country'
                  options={convertStaticDataToOptions(staticData.countries)}
                  itemToString={item => item && item.label}
                  disabled={get(form.values, 'invoicingEntity.value')}
                  component={Autocomplete}
                />
                <Field
                  name='parentAccount'
                  label='Parent account'
                  component={Autocomplete}
                  loadOptions={this.handleParentAccountAutocomplete}
                  onChange={item => this.handleParentAccountChange(item, form)}
                  itemToString={item => item && item.label}
                />
                <Field
                  name='account'
                  label='Account'
                  component={Autocomplete}
                  disabled={!get(form.values, 'parentAccount.value')}
                  loadOptions={() => this.handleAccountAutocomplete(form)}
                  onChange={item => this.handleAccountChange(item, form)}
                  itemToString={item => item && item.label}
                />
                <Field
                  name='project'
                  label='Project'
                  component={Autocomplete}
                  disabled={!get(form.values, 'account.value')}
                  loadOptions={input =>
                    this.handleProjectAutocomplete(input, form)
                  }
                  onChange={item => this.handleProjectChange(item, form)}
                  itemToString={item => item && item.label}
                />
                <Field
                  name='contract'
                  label='Contract'
                  component={Autocomplete}
                  disabled={!get(form.values, 'parentAccount.value')}
                  loadOptions={item =>
                    this.handleContractAutocomplete(item, form)
                  }
                  itemToString={item => item && item.label}
                  onChange={item => this.handleContractChange(item, form)}
                />
                <Field
                  name='invoicingEntity'
                  label='Invoicing entity'
                  itemToString={this.renderInvoicingEntity}
                  loadOptions={input =>
                    this.handleInvoicinigEntityAutocomplete(input, form)
                  }
                  disabled={!get(form.values, 'parentAccount.value')}
                  onChange={item =>
                    this.handleInvoicingEntityChange(item, form)
                  }
                  component={Autocomplete}
                />
              </div>
              <div className='expert-form__section'>
                <Button
                  primary
                  submit
                  onClick={this.onSubmit}
                  disabled={form.isSubmitting}
                >
                  Save
                </Button>
                <Button onClick={modalStore.close} disabled={form.isSubmitting}>
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        )}
      />
    )
  }

  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: true
  }
}

function required (label) {
  return (
    <span>
      {label} <strong className='required-marker'>*</strong>
    </span>
  )
}

export default inject(
  'modalStore',
  'growlStore',
  'financialRevenueStore',
  'staticData',
  'atheneumOfficeStore'
)(GeneralAdjustmentForm)
